/**
 * Find Company Handler
 * Query handler for finding companies by ID or name using GraphQL
 */

import type { ToolExecutionContext } from '../../types/index.js';
import { QueryHandlerBase } from '../base/index.js';
import { stringField, uuidField } from '../../utils/schema-builder.js';
import { createUserGraphQLClient } from '../../utils/graphql-client.js';

/**
 * Find company handler
 * Uses GraphQL queries to search for companies with RLS enforcement
 */
export class FindCompanyHandler extends QueryHandlerBase<any> {
  id = 'find_company';

  schema = {
    type: 'object',
    properties: {
      id: uuidField('UUID of the company (optional if name is provided)'),
      name: stringField('Company name to search for (optional if id is provided)'),
    },
    required: [],
  };

  async execute(params: Record<string, any>, context?: ToolExecutionContext): Promise<any> {
    if (!params.id && !params.name) {
      throw new Error('Either id or name must be provided');
    }

    if (!context?.userToken) {
      throw new Error('User token is required for company search');
    }

    try {
      // Dynamically import GraphQL types and documents to avoid circular dependencies
      const { GetCompanyDocument, GetCompaniessDocument } = await import('@/src/gql/graphql');
      
      // Create user-scoped GraphQL client that respects RLS
      const client = createUserGraphQLClient(context.userToken);

      if (params.id) {
        // Search by ID using GraphQL
        const result = await client.query({
          query: GetCompanyDocument,
          variables: {
            id: params.id,
          },
          fetchPolicy: 'network-only',
        });

        if (result.error) {
          throw new Error(`GraphQL error: ${result.error.message}`);
        }

        const data = result.data as any;
        const company = data?.companiesCollection?.edges?.[0]?.node;
        if (!company) {
          return {
            error: `Company with ID "${params.id}" not found`,
            id: params.id,
          };
        }

        return {
          ...company,
          viewInApp: `[View Company](/companies/${company.id})`,
        };
      } else {
        // Search by name using GraphQL with filter
        const result = await client.query({
          query: GetCompaniessDocument,
          variables: {
            first: 5,
            filter: {
              name: {
                ilike: `%${params.name}%`,
              },
            },
          },
          fetchPolicy: 'network-only',
        });

        if (result.error) {
          throw new Error(`GraphQL error: ${result.error.message}`);
        }

        const data = result.data as any;
        const companies = data?.companiesCollection?.edges?.map((edge: any) => edge?.node).filter((node: any) => node != null) || [];

        if (companies.length === 0) {
          return {
            error: `No companies found matching "${params.name}"`,
            query: params.name,
          };
        }

        const viewInApp = (c: any) => `[${c.name || 'Company'}](/companies/${c.id})`;
        if (companies.length === 1) {
          return { ...companies[0], viewInApp: viewInApp(companies[0]) };
        }
        return {
          matches: companies.map((c: any) => ({ ...c, viewInApp: viewInApp(c) })),
          count: companies.length,
          viewInApp: companies.map(viewInApp).join(' · '),
        };
      }
    } catch (error) {
      throw new Error(`Company search failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}
